import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FaIcons from 'react-icons/fa';
import './PricingSection.css';

// Create icon components with proper typing
const FaCheck = FaIcons.FaCheck as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaRobot = FaIcons.FaRobot as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaPlane = FaIcons.FaPlane as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const PricingSection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  // Price plan features
  const features = [
    'All materials and equipment provided',
    'Take-home kit',
    'Certificate of completion',
    'Final day showcase event',
    '24/7 online support'
  ];

  return (
    <section className="pricing-section" id="pricing">
      <div className="container">
        <motion.div
          className="pricing-header"
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeIn}
        >
          <h2 className="section-title text-center" style={{
            background: 'linear-gradient(135deg, #6366f1, #10b981)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block',
            marginBottom: '1rem'
          }}>
            Pricing Plans
          </h2>
          <p className="section-subtitle" style={{ color: '#000000', maxWidth: '700px', margin: '0 auto' }}>
            Choose the perfect plan for your child's summer of innovation and learning.
          </p>
          
          <div className="regular-price-banner">
            <div className="price-text">
              Registration: ₹5,000 + 18% GST (Kit Included)
            </div>
          </div>
        </motion.div>

        <motion.div
          className="pricing-cards"
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div 
            className="pricing-card popular" 
            variants={cardVariants}
          >
            <div className="popular-badge">Popular</div>
            <div className="pricing-icon">
              <FaRobot />
            </div>
            <h3 className="pricing-title">Robotics + IoT + AI</h3>
            <div className="pricing-price">
              <div className="price-options" style={{ textAlign: 'center' }}>
                <div className="price-option" style={{ flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '1rem' }}>₹11,999</span>
                  <span className="price" style={{ fontSize: '2rem', fontWeight: 700 }}>
                    ₹5,000
                  </span>
                  <span className="option-label" style={{ fontSize: '0.85rem' }}>
                    + 18% GST
                  </span>
                  <span style={{ display: 'inline-block', background: '#fbbf24', color: '#0a1f44', padding: '4px 12px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 800, marginTop: 10 }}>
                    Save ₹6,999
                  </span>
                </div>
              </div>
            </div>
            <p className="pricing-description">
              Perfect for tech enthusiasts interested in programming robots.
            </p>
            <ul className="pricing-features">
              {features.map((feature, index) => (
                <li key={index}>
                  <span className="feature-icon"><FaCheck /></span>
                  <span className="feature-text">{feature.replace('kit', 'Arduino kit')}</span>
                </li>
              ))}
            </ul>
            <Link to="/register?track=robotics" className="btn btn-primary pricing-cta">
              Register Now
            </Link>
          </motion.div>

          <motion.div 
            className="pricing-card" 
            variants={cardVariants}
          >
            <div className="pricing-icon">
              <FaPlane />
            </div>
            <h3 className="pricing-title">Drone Tech + Aero</h3>
            <div className="pricing-price">
              <div className="price-options" style={{ textAlign: 'center' }}>
                <div className="price-option" style={{ flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '1rem' }}>₹11,999</span>
                  <span className="price" style={{ fontSize: '2rem', fontWeight: 700 }}>
                    ₹5,000
                  </span>
                  <span className="option-label" style={{ fontSize: '0.85rem' }}>
                    + 18% GST
                  </span>
                  <span style={{ display: 'inline-block', background: '#fbbf24', color: '#0a1f44', padding: '4px 12px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 800, marginTop: 10 }}>
                    Save ₹6,999
                  </span>
                </div>
              </div>
            </div>
            <p className="pricing-description">
              For aviation enthusiasts who want to build and fly their own drones.
            </p>
            <ul className="pricing-features">
              {features.map((feature, index) => (
                <li key={index}>
                  <span className="feature-icon"><FaCheck /></span>
                  <span className="feature-text">{feature.replace('kit', 'mini drone kit').replace('showcase event', 'drone race event')}</span>
                </li>
              ))}
            </ul>
            <Link to="/register?track=drone" className="btn btn-primary pricing-cta">
              Register Now
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
